import { ThreeDKeys } from 'src/app/core/constants/3D/3d-keys';
import { Simulation } from 'src/app/core/models/simulation.model';
import { Clone } from 'src/app/shared/utils/clone';
import { PMath } from 'src/app/shared/utils/recurrent-math/project-math';
import { CircleGeometry, Mesh, MeshBasicMaterial } from 'three';
import { Text } from 'troika-three-text';
import { Catenary3D } from '../../catenary-answer';
import { Connection } from '../../connection.model';
import { ReferenceType } from '../../reference-type.enum';
import ObjectBuilder from './object-builder.model';

export class ConnectionBuilder extends ObjectBuilder {
  constructor() {
    super();
  }

  private get connection(): Mesh {
    return this._object;
  }

  private set connection(connection) {
    this._object = connection;
  }

  async build(riser: Catenary3D, simulation: Simulation, connection: Connection): Promise<Mesh> {
    const material = this.createMaterial(riser, connection);
    const geometry = this.createGeometry(riser, connection);

    this.connection = new Mesh(geometry, material);
    this.connection.name = `[${connection.title}].${connection.internalId}`;
    const globalConnection = this.getAsGlobal(connection, simulation);
    this.connection.position.set(
      globalConnection.localCoordinates.x,
      globalConnection.localCoordinates.y,
      globalConnection.localCoordinates.z,
    );
    this.putLabel(this.connection, connection);
    return this.connection;
  }

  getAsGlobal(connection: Connection, simulation: Simulation): Connection {
    if (connection.reference === ReferenceType.GLOBAL) {
      return connection;
    }

    const targetFloatings = simulation.floating.filter(
      (f) => f.internalId === connection.referenceId,
    );
    const floating = targetFloatings[0] || null;

    let newX = connection?.localCoordinates?.x;
    let newY = connection?.localCoordinates?.y;
    let newZ = connection?.localCoordinates?.z;

    if (floating) {
      const rotation = PMath.azimuthDegToRotationRad(floating.azimuth);
      const floatingX = floating?.origin?.x;
      const floatingY = floating?.origin.y;
      const depth = simulation.global?.length > 0 ? simulation.global[0].seabedDepth : 0;
      const floatingZ = depth - floating?.draft;

      const localX = newX;
      const localY = newY;
      newX = floatingX + localX * Math.cos(rotation) - localY * Math.sin(rotation);
      newY = floatingY + localX * Math.sin(rotation) + localY * Math.cos(rotation);
      newZ += floatingZ;
    }

    const connectionCopy = Clone(connection) as Connection;
    connectionCopy.localCoordinates.x = newX;
    connectionCopy.localCoordinates.y = newY;
    connectionCopy.localCoordinates.z = newZ;
    return connectionCopy;
  }

  createGeometry(_catenary3D: Catenary3D, _connection: Connection): CircleGeometry {
    return new CircleGeometry(2, 12);
  }

  createMaterial(_catenary3D: Catenary3D, connection: Connection): MeshBasicMaterial {
    return new MeshBasicMaterial({ color: connection.graphicProperties.color });
  }

  putLabel(object3d, connection: Connection) {
    const label: Text = new Text();
    label.text = connection.title;
    label.name = ThreeDKeys.THREEJS_OBJECTS_LABEL_NAME;
    label.position.x = 5;
    label.fontSize = 10;
    label.color = connection.graphicProperties.color;
    object3d.add(label);
  }

  transform(connection: Connection): void {}

  focus(): void {}
}
